import styles from './ChatPanel.module.scss';
import { Avatar, useMobile } from '@app/ui';
import { Avatar as AntdAvatar, Button, List } from 'antd';
import {
  ChatGroupCustomizeValue,
  ChatHeaderActions,
  Chatify,
  Icon,
  Localizer,
  chatifyApi,
  defaultLocalizer,
  useChatContext,
  useConversation,
} from '@webinex/chatify';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { ChatSettings } from '@app/chat/settings';
import { PinChatButton } from '@app/chat/widgets/Chat.Global';
import { ChatGroupAside } from './ChatGroupAside';
import { CreateChatMemberForm } from './CreateChat';
import { DAYJS_FORMAT } from '@app/base';
import { useUserContextSelector } from '@app/main/utils';
import { ChatMemberListItem, ChatMemberListItemProps } from './ChatMemberListItem';
import { useChatAllowedMemberIds } from './CreateChat/useChatAllowedMemberIds';

export function useDefaultChatCustomize() {
  return useMemo<ChatGroupCustomizeValue>(
    () => ({
      ChatHeaderActions: () => {
        const { id } = useChatContext();
        const { isMobile } = useMobile();

        return (
          <>
            <ChatHeaderActions.Component />
            {!isMobile && <PinChatButton chatId={id} />}
          </>
        );
      },
      ChatMembersButton: () => {
        const { showMembers, onShowMembers } = useChatContext();
        const [isExactPatient, isExactCareHomeStaff] = useUserContextSelector((x) => [
          x.roles.length === 1 && x.hasRole('PATIENT'),
          x.roles.length === 1 && x.hasRole('CARE_HOME_STAFF'),
        ]);

        return (
          !isExactPatient &&
          !isExactCareHomeStaff && (
            <Button onClick={() => onShowMembers(!showMembers)} type="link" icon={<Icon type="members" />} />
          )
        );
      },
      CreateChatMemberInput: (props) => <CreateChatMemberForm {...props} />,
      Avatar: ({ account }) => <Avatar size="sm" alt={account.name} reference={account.avatar} />,
      ChatGroupAside: () => <ChatGroupAside />,
      ChatMemberListItem: (props: ChatMemberListItemProps) => <ChatMemberListItem {...props} />,
      ChatMembersPanel: () => {
        const { id } = useChatContext();
        const { data: chat } = chatifyApi.useGetChatQuery({ id });
        const { allowedIds, isLoading } = useChatAllowedMemberIds();

        // Pass ids to the server so only authorized accounts are fetched.
        // Skip while allowedIds is still resolving so we never request the unfiltered list.
        const { data: accounts } = chatifyApi.useGetAccountListQuery(
          { ids: allowedIds },
          { skip: allowedIds === undefined && isLoading },
        );

        const ordered = useMemo(
          () =>
            accounts
              ?.slice()
              .sort(
                (a, b) =>
                  (chat?.members.some((m) => m.id === a.id) ? -100 : 0) -
                  (chat?.members.some((m) => m.id === b.id) ? -100 : 0) +
                  a.name.localeCompare(b.name),
              ),
          [accounts, chat],
        );

        if (!chat?.members || !accounts) {
          return null;
        }

        return (
          <div className="wxchtf-chat-members-panel">
            <List
              className="wxchtf-chat-members-list"
              dataSource={ordered}
              renderItem={(item) => <ChatMemberListItem id={item.id} chatId={id} accounts={ordered} />}
            />
          </div>
        );
      },
      ChatMembersPreview: () => {
        const { id } = useChatContext();
        const { data: chat } = chatifyApi.useGetChatQuery({ id });
        const members = chat?.members ?? [];

        return (
          <AntdAvatar.Group
            max={{
              count: 4,
              style: {
                height: 30,
                width: 30,
                background: 'var(--info-background-color)',
                color: 'var(--info-color)',
                fontSize: 12,
              },
            }}
          >
            {members.map((member) => (
              <Avatar key={member.id} size="xs" alt={member.name} reference={member.avatar} />
            ))}
          </AntdAvatar.Group>
        );
      },
      MessageInfoBox: (props) => {
        const {
          message: { sentBy, sentAt },
        } = props;
        const { id, isReading, lastReadMessageId } = useConversation();
        const userId = useUserContextSelector((x) => x.id);
        const my = userId === sentBy.id;
        const isRead = lastReadMessageId != null && id.localeCompare(lastReadMessageId) <= 0;
        const reading = !isRead && isReading?.(id);

        return (
          <div className="wxchtf-message-info-box">
            <div className="wxchtf-sending-message-sending-box">{!my && sentBy.name}</div>
            <div className="wxchtf-message-sent-at">
              {localizer.isToday(sentAt) ? localizer.timestamp(sentAt) : localizer.dateTime(sentAt)}
            </div>
            {!my && (
              <div className="wxchtf-message-read-box">
                {reading ? localizer.message.reading() : localizer.message.read()}
              </div>
            )}
          </div>
        );
      },
      flippo: ChatSettings.flippo,
    }),
    [],
  );
}

const localizer: Localizer = {
  ...defaultLocalizer,
  timestamp: (value) => dayjs.tz(value).format(DAYJS_FORMAT.TIME),
  isToday: (value) => dayjs.tz(value).startOf('date').isSame(dayjs.tz().startOf('date')),
  dateTime: (value) => dayjs.tz(value).format(DAYJS_FORMAT.DATE_TIME),
};

export function ChatPanel() {
  const customize = useDefaultChatCustomize();

  return <Chatify.ChatGroup className={styles.chat} customize={customize} localizer={localizer} />;
}
